import { Alert } from '@creditornot/cb-alert';
import { colors } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { getCookieName } from '@creditornot/cwc-cookie-sdk';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { useCookie } from 'react-use';
import styled from 'styled-components';

import { createContract, getOnboardingReviewInfo } from 'apis/merchant-onboarding-service/api';
import { useTelemetryViewEvent } from 'common/hooks';
import { typographyCss } from 'common/typography';
import { ssmoAgreementReviewInteractionClickConfirm, ssmoAgreementReviewView } from 'core/telemetry/avo/Avo';
import { InboundMerchant } from 'modules/onboarding-page/types';

import { useRegisterTelemetryEvent } from '../../../../core/telemetry/hooks/useRegisterTelemetryEvent';
import { useCookieConsents } from '../../../cookie-consents/CookieConsentsProvider';
import { CountryConfig } from '../../../country/types';
import { SsmoStepsIdentifiers } from '../../constants';
import { RenderPrice } from '../RenderPrice';

type ReviewInfoProps = {
  goToNextStep: () => void;
  inboundMerchant: InboundMerchant;
  countryConfig: CountryConfig;
  setUpdateIsInProgress: (inProgress: boolean) => void;
};

const Wrapper = styled.form`
  width: 100%;
  margin-block-end: 56px;
`;

const DetailTitle = styled.h6`
  ${typographyCss.Title1}
  margin-block: 32px 4px;
  font-weight: 700;
`;

const DetailsContainer = styled.div``;

export const Row = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 0;

  & + & {
    border-block-start: 1px solid ${cssThemedValue({ light: colors.pepper16, dark: colors.salt72 })};
  }
`;

export const Label = styled.div`
  ${typographyCss.Body3}
  line-height: 20px;
`;

export const Value = styled.div`
  ${typographyCss.Body3}
  display: flex;
  flex-direction: row;
  text-align: end;
`;

const StyledAlert = styled(Alert)`
  margin-block-start: 24px;
`;

const StyledSpinner = styled(Spinner)`
  margin-inline: auto;
  margin-block: 24px;
`;

const ReviewInfo = ({ setUpdateIsInProgress, countryConfig, inboundMerchant }: ReviewInfoProps) => {
  const NOT_APPLICABLE_VALUE = '-';

  useTelemetryViewEvent('ssmo_agreement_review_view', {}, durationMs => ssmoAgreementReviewView({ durationMs }));

  const history = useHistory();
  const { formatMessage } = useIntl();

  const [contractCreationError, setContractCreationError] = useState(null);

  const { merchantId } = inboundMerchant;

  const register = useRegisterTelemetryEvent();
  const {
    consents: { analytics: analyticsConsent, marketing: marketingConsent },
  } = useCookieConsents();
  const [woltUid] = useCookie(getCookieName('wolt-uid'));
  const { country } = inboundMerchant;

  const {
    isLoading: reviewInfoIsLoading,
    data: reviewInfo,
    error: reviewInfoError,
  } = useQuery(['/merchant-admin/me/contract-review'], getOnboardingReviewInfo);

  if (reviewInfoIsLoading) {
    return <StyledSpinner size="large" />;
  }

  if (reviewInfoError || !reviewInfo) {
    return <StyledAlert variant="error" title="Failed to load review info." />;
  }

  const { currency, primary_language } = countryConfig;

  const handleCreateContract = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setUpdateIsInProgress(true);

      register({
        event: 'review_contract_info_submitted',
        marketingConsent,
        analyticsConsent,
        properties: {
          __woltUid: woltUid,
          country,
        },
      });

      await createContract(merchantId);

      ssmoAgreementReviewInteractionClickConfirm();
      setContractCreationError(null);

      history.push('/onboarding-page/sign-contract');
    } catch (error) {
      setContractCreationError(error.message);
    } finally {
      setUpdateIsInProgress(false);
    }
  };

  const renderPercentageValue = (value: string) =>
    value !== NOT_APPLICABLE_VALUE ? `${value}%` : NOT_APPLICABLE_VALUE;

  const isValueApplied = (value: string) => value !== NOT_APPLICABLE_VALUE;

  return (
    <Wrapper onSubmit={handleCreateContract} id={SsmoStepsIdentifiers.Review}>
      <DetailsContainer>
        <DetailTitle>{formatMessage({ id: 'self-serve-sign-up.wizard.review-info.business-details' })}</DetailTitle>

        <Row>
          <Label>{formatMessage({ id: 'self-serve-sign-up.wizard.business-info.restaurant' })}</Label>
          <Value aria-label="Restaurant name">{reviewInfo.venueName}</Value>
        </Row>

        <Row>
          <Label>{formatMessage({ id: 'self-serve-sign-up.wizard.review-info.restaurant-address' })}</Label>
          <Value aria-label="Restaurant address">
            <span>{reviewInfo.venueAddress}</span>
            &nbsp;
            <span>
              {reviewInfo.venuePostCode}, {reviewInfo.venueCity}
            </span>
          </Value>
        </Row>

        <Row>
          <Label>{formatMessage({ id: 'self-serve-sign-up.wizard.business-info.business-name' })}</Label>
          <Value aria-label="Business name">{reviewInfo.merchantName}</Value>
        </Row>

        <Row>
          <Label>{formatMessage({ id: 'self-serve-sign-up.wizard.business-info.business-address' })}</Label>
          <Value aria-label="Business address">
            <span>{reviewInfo.merchantAddress}</span>
            &nbsp;
            <span>
              {reviewInfo.merchantPostCode}, {reviewInfo.merchantCity}
            </span>
          </Value>
        </Row>

        <Row>
          <Label>{formatMessage({ id: 'self-serve-sign-up.wizard.business-info.business-id' })}</Label>
          <Value aria-label="Business ID">{reviewInfo.businessId}</Value>
        </Row>

        <DetailTitle>{formatMessage({ id: 'self-serve-sign-up.wizard.review-info.service-package' })}</DetailTitle>

        <Row>
          <Label>Wolt delivery</Label>
          <Value aria-label="Wolt delivery">{renderPercentageValue(reviewInfo.deliveryCommission)}</Value>
        </Row>

        <Row>
          <Label>Takeaway</Label>
          <Value aria-label="Takeaway">{renderPercentageValue(reviewInfo.takeawayCommission)}</Value>
        </Row>
        {isValueApplied(reviewInfo.subscriptionCommission) && (
          <Row>
            <Label>Wolt+</Label>
            <Value aria-label="Wolt+">{renderPercentageValue(reviewInfo.subscriptionCommission)}</Value>
          </Row>
        )}

        {isValueApplied(reviewInfo.tabletFee) && (
          <Row>
            <Label>Wolt tablet</Label>
            <Value aria-label="Wolt tablet">
              <RenderPrice primaryLanguage={primary_language} value={reviewInfo.tabletFee} currency={currency} />
            </Value>
          </Row>
        )}
        {isValueApplied(reviewInfo.printerFee) && (
          <Row>
            <Label>{formatMessage({ id: 'self-serve-sign-up.wizard.review-info.wolt-printer' })}</Label>
            <Value aria-label="Wolt printer">
              <RenderPrice primaryLanguage={primary_language} value={reviewInfo.printerFee} currency={currency} />
            </Value>
          </Row>
        )}
        {isValueApplied(reviewInfo.simCardFee) && (
          <Row>
            <Label>{formatMessage({ id: 'self-serve-sign-up.wizard.review-info.mobile-data' })}</Label>
            <Value aria-label="Mobile data">
              <RenderPrice
                primaryLanguage={primary_language}
                value={reviewInfo.simCardFee}
                currency={currency}
                isPricePerWeek
              />
            </Value>
          </Row>
        )}
        {isValueApplied(reviewInfo.photographFee) && (
          <Row>
            <Label>{formatMessage({ id: 'self-serve-sign-up.wizard.review-info.professional-photoshoot' })}</Label>
            <Value aria-label="Professional photoshoot">
              <RenderPrice primaryLanguage={primary_language} value={reviewInfo.photographFee} currency={currency} />
            </Value>
          </Row>
        )}
      </DetailsContainer>

      <StyledAlert
        variant="warning"
        title={formatMessage({ id: 'self-serve-sign-up.wizard.review-info.warning-title' })}
      >
        {formatMessage({ id: 'self-serve-sign-up.wizard.review-info.warning-text' })}
      </StyledAlert>

      {Boolean(contractCreationError) && (
        <StyledAlert variant="error" title={formatMessage({ id: 'self-serve-sign-up.wizard.review-info.fail-title' })}>
          {contractCreationError}
        </StyledAlert>
      )}
    </Wrapper>
  );
};

export default ReviewInfo;
